import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/utils/api";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, LineChart, Line,
} from "recharts";
import {
  ArrowLeft, User, Zap, CheckCircle2, XCircle, TrendingUp, Search, X
} from "lucide-react";

const OperatorPerformance = () => {
  const navigate = useNavigate();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drill, setDrill] = useState(null); // operator obj
  const [q, setQ] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/operator-performance?days=${days}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [days]);

  const filteredOps = (data?.operators || []).filter((o) =>
    !q || (o.operator_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (o.operator_id || "").toLowerCase().includes(q.toLowerCase())
  );

  // Chart-ready: top 10 by OK for the bar chart
  const topOps = [...filteredOps].slice(0, 10).map((o) => ({
    name: (o.operator_name || o.operator_id).split(" ")[0].slice(0, 12),
    ok: o.ok_qty,
    ng: o.ng_qty,
  }));

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto p-6">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 mb-4"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900 inline-flex items-center gap-2">
              <User size={22} className="text-emerald-700" /> Operator Performance
            </h1>
            <p className="text-sm text-slate-500">Per-operator OK / NG / stroke totals with drill-down.</p>
          </div>
          <div className="flex items-center gap-2">
            <select
              data-testid="opperf-range"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value))}
              className="border-slate-300 rounded-lg text-sm px-2 py-1.5"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
        </div>

        {/* KPIs */}
        {data && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
            <Kpi label="Operators" value={data.totals.operators_count} color="slate" />
            <Kpi label="OK Parts" value={data.totals.ok_qty} icon={<CheckCircle2 size={16} />} color="emerald" />
            <Kpi label="NG Parts" value={data.totals.ng_qty} icon={<XCircle size={16} />} color="red" />
            <Kpi label="Rejection %" value={`${data.totals.rejection_pct}%`} color={data.totals.rejection_pct > 5 ? "red" : "emerald"} />
            <Kpi label="Strokes" value={data.totals.strokes} icon={<Zap size={16} />} color="slate" />
          </div>
        )}

        {/* Bar chart */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 mb-4">
          <p className="text-xs font-extrabold uppercase tracking-widest text-slate-400 mb-2">
            Top operators (OK vs NG)
          </p>
          {topOps.length === 0 ? (
            <div className="text-center text-slate-400 py-6 text-sm">No production yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={topOps} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="ok" name="OK" fill="#15803D" />
                <Bar dataKey="ng" name="NG" fill="#DC2626" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Search */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 mb-4 flex items-center gap-2">
          <Search size={16} className="text-slate-400" />
          <input
            data-testid="opperf-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search operator by name or ID..."
            className="flex-1 text-sm outline-none"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-2 text-left">Operator</th>
                  <th className="px-4 py-2 text-right">Jobs</th>
                  <th className="px-4 py-2 text-right">OK</th>
                  <th className="px-4 py-2 text-right">NG</th>
                  <th className="px-4 py-2 text-right">Rejection %</th>
                  <th className="px-4 py-2 text-right">Strokes</th>
                  <th className="px-4 py-2 text-right">Setup</th>
                  <th className="px-4 py-2 text-left">Machines</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody data-testid="opperf-tbody">
                {loading ? (
                  <tr><td colSpan={9} className="text-center text-slate-400 py-6">Loading...</td></tr>
                ) : filteredOps.length === 0 ? (
                  <tr><td colSpan={9} className="text-center text-slate-400 py-6">No operators in this range</td></tr>
                ) : filteredOps.map((o) => (
                  <tr key={o.operator_id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setDrill(o)}>
                    <td className="px-4 py-2 font-bold text-slate-900">
                      <div>{o.operator_name}</div>
                      <div className="text-[10px] text-slate-400">{o.operator_id}</div>
                    </td>
                    <td className="px-4 py-2 text-right">{o.jobs_count}</td>
                    <td className="px-4 py-2 text-right font-extrabold text-emerald-700">{o.ok_qty}</td>
                    <td className="px-4 py-2 text-right font-extrabold text-red-600">{o.ng_qty}</td>
                    <td className="px-4 py-2 text-right">
                      <span className={o.rejection_pct > 5 ? "text-red-600 font-extrabold" : "text-slate-600"}>{o.rejection_pct}%</span>
                    </td>
                    <td className="px-4 py-2 text-right">{o.strokes}</td>
                    <td className="px-4 py-2 text-right">{o.setup_strokes || 0}</td>
                    <td className="px-4 py-2 text-xs text-slate-500 truncate max-w-xs">{o.machines.join(", ") || "—"}</td>
                    <td className="px-4 py-2 text-right">
                      <TrendingUp size={14} className="text-slate-400" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {drill && (
          <DrillModal op={drill} onClose={() => setDrill(null)} />
        )}
      </div>
    </div>
  );
};

const Kpi = ({ label, value, icon, color = "slate" }) => {
  const map = {
    emerald: "text-emerald-700 bg-emerald-100",
    red: "text-red-700 bg-red-100",
    slate: "text-slate-700 bg-slate-100",
  };
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</p>
        {icon && <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${map[color]}`}>{icon}</div>}
      </div>
      <p className="text-2xl font-extrabold text-slate-900 truncate">{value}</p>
    </div>
  );
};

const DrillModal = ({ op, onClose }) => (
  <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
      <div className="bg-[#1A3560] text-white px-5 py-4 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest opacity-80">Operator</p>
          <p className="font-extrabold text-lg">{op.operator_name} <span className="text-white/70 font-normal text-sm">({op.operator_id})</span></p>
        </div>
        <button onClick={onClose} className="bg-white/20 hover:bg-white/30 rounded-full p-1.5">
          <X size={16} />
        </button>
      </div>
      <div className="p-5 overflow-y-auto max-h-[70vh]">
        <div className="grid grid-cols-4 gap-2 mb-4 text-center">
          <div className="bg-emerald-50 rounded-lg p-2"><p className="text-[10px] font-bold text-emerald-700">OK</p><p className="font-extrabold text-emerald-700">{op.ok_qty}</p></div>
          <div className="bg-red-50 rounded-lg p-2"><p className="text-[10px] font-bold text-red-700">NG</p><p className="font-extrabold text-red-700">{op.ng_qty}</p></div>
          <div className="bg-slate-50 rounded-lg p-2"><p className="text-[10px] font-bold text-slate-700">Strokes</p><p className="font-extrabold text-slate-700">{op.strokes}</p></div>
          <div className="bg-amber-50 rounded-lg p-2"><p className="text-[10px] font-bold text-amber-700">Setup</p><p className="font-extrabold text-amber-700">{op.setup_strokes || 0}</p></div>
        </div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Per-day output</p>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={op.per_day} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="ok_qty" name="OK" stroke="#15803D" strokeWidth={2} dot />
            <Line type="monotone" dataKey="ng_qty" name="NG" stroke="#DC2626" strokeWidth={2} dot />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
          <div>
            <p className="font-bold text-slate-700 mb-1">Machines used</p>
            <p className="text-slate-500">{op.machines.join(", ") || "—"}</p>
          </div>
          <div>
            <p className="font-bold text-slate-700 mb-1">Parts produced</p>
            <p className="text-slate-500">{op.parts.join(", ") || "—"}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default OperatorPerformance;
